import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { api, resolveImg } from "../../lib/api";
import { Field, TextInput } from "./fields";

const FONTS = ["Outfit", "Inter", "Space Grotesk", "Sora"];

const ColorField = ({ label, value, onChange }) => (
  <Field label={label}>
    <div className="flex items-center gap-3">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 cursor-pointer rounded border bg-transparent" style={{ borderColor: "var(--border-color)" }} />
      <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </Field>
);

export const SettingsManager = () => {
  const { settings, update } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  if (!form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("about_photo", data.url);
      toast.success("Photo uploaded — click Save Settings to apply");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      update(data);
      toast.success("Settings saved — theme updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="settings-manager">
      <section>
        <h3 className="font-heading text-lg font-medium mb-5">Theme</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <ColorField label="Accent color" value={form.accent_color} onChange={(v) => set("accent_color", v)} />
          <ColorField label="Background" value={form.bg_color} onChange={(v) => set("bg_color", v)} />
          <ColorField label="Surface" value={form.surface_color} onChange={(v) => set("surface_color", v)} />
          <ColorField label="Primary text" value={form.text_primary} onChange={(v) => set("text_primary", v)} />
          <ColorField label="Secondary text" value={form.text_secondary} onChange={(v) => set("text_secondary", v)} />
          <ColorField label="Border" value={form.border_color} onChange={(v) => set("border_color", v)} />
          <Field label="Heading font">
            <select value={form.font_heading} onChange={(e) => set("font_heading", e.target.value)} className="w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm outline-none" style={{ borderColor: "var(--border-color)" }} data-testid="settings-font-heading">
              {FONTS.map((f) => <option key={f} value={f} style={{ color: "#000" }}>{f}</option>)}
            </select>
          </Field>
          <Field label="Body font">
            <select value={form.font_body} onChange={(e) => set("font_body", e.target.value)} className="w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm outline-none" style={{ borderColor: "var(--border-color)" }} data-testid="settings-font-body">
              {FONTS.map((f) => <option key={f} value={f} style={{ color: "#000" }}>{f}</option>)}
            </select>
          </Field>
          <Field label="Button radius" hint="e.g. 9999px (pill), 8px, 0px">
            <TextInput value={form.button_radius} onChange={(e) => set("button_radius", e.target.value)} data-testid="settings-btn-radius" />
          </Field>
        </div>
      </section>

      <section>
        <h3 className="font-heading text-lg font-medium mb-5">About Portrait</h3>
        <div className="grid md:grid-cols-2 gap-5 items-start">
          <Field label="Portrait image" hint="Paste an image URL or upload a file (used in the About section)">
            <div className="flex flex-col sm:flex-row gap-3">
              <TextInput value={form.about_photo || ""} onChange={(e) => set("about_photo", e.target.value)} data-testid="settings-about-photo" placeholder="https://… or /about-portrait.jpg" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="settings-about-file" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm hover:border-[var(--accent-color)] transition-colors disabled:opacity-60"
                style={{ borderColor: "var(--border-color)" }}
                data-testid="settings-about-upload-btn"
              >
                <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </Field>
          {form.about_photo && (
            <img src={resolveImg(form.about_photo)} alt="About preview" className="h-48 w-40 rounded-2xl border object-cover" style={{ borderColor: "var(--border-color)" }} data-testid="settings-about-preview" />
          )}
        </div>
      </section>

      <section>
        <h3 className="font-heading text-lg font-medium mb-5">Links & Brand</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Brand name"><TextInput value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="settings-brand" /></Field>
          <Field label="Tally form URL" hint="Opens as a modal on 'Start a Project'"><TextInput value={form.tally_url || ""} onChange={(e) => set("tally_url", e.target.value)} data-testid="settings-tally" /></Field>
          <Field label="Instagram profile URL" hint="Used by 'Message Me' and the handle link"><TextInput value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} data-testid="settings-instagram" /></Field>
          <Field label="Instagram handle (display)"><TextInput value={form.instagram_handle || ""} onChange={(e) => set("instagram_handle", e.target.value)} data-testid="settings-instagram-handle" /></Field>
          <Field label="X / Twitter URL"><TextInput value={form.twitter_url || ""} onChange={(e) => set("twitter_url", e.target.value)} data-testid="settings-twitter" /></Field>
          <Field label="YouTube URL (optional)"><TextInput value={form.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} data-testid="settings-youtube" /></Field>
          <Field label="Email"><TextInput value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" /></Field>
        </div>
      </section>

      <button onClick={save} disabled={saving} className="rounded-full px-8 py-3 text-sm font-medium text-black hover:opacity-90 disabled:opacity-60" style={{ background: "var(--accent-color)" }} data-testid="settings-save">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
};
